/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  notificationApi,
  Notification,
} from '../../services/notificationApi';

interface NotificationState {
  notifications: Notification[];
  total: number;
  page: number;
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  total: 0,
  page: 1,
  unreadCount: 0,
  loading: false,
  error: null,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (
    params:
      | { page?: number; page_size?: number; unread_only?: boolean }
      | undefined,
    { rejectWithValue }
  ) => {
    try {
      return await notificationApi.getNotifications(params);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch notifications'
      );
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationApi.getUnreadCount();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch unread count'
      );
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (id: number, { rejectWithValue }) => {
    try {
      return await notificationApi.markAsRead(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to mark notification as read'
      );
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationApi.markAllRead();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          'Failed to mark all notifications as read'
      );
    }
  }
);

// Slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.loading = false;
      state.notifications = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.unreadCount = action.payload.unread_count;
    });
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload.unread_count;
    });

    // Mark as read
    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const idx = state.notifications.findIndex(
        (n) => n.id === action.payload.id
      );
      if (idx !== -1) {
        state.notifications[idx] = action.payload;
      }
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    });

    // Mark all read
    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.notifications = state.notifications.map((n) => ({
        ...n,
        is_read: true,
      }));
      state.unreadCount = 0;
    });
  },
});

export const { clearError } = notificationSlice.actions;
export default notificationSlice.reducer;
